import { motion } from 'framer-motion';
import AnimatedCounter from './AnimatedCounter';
import { ArrowDown } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_digital-asset-io/artifacts/w5ymcp91_IODX%20logo%20with%20blue%20gradient%20background.png';

const stats = [
  { end: 180, suffix: ' mg/L', label: 'Peak Concentration' },
  { end: 4, suffix: '+', label: 'Patent Filings' },
  { end: 12, suffix: '+', label: 'Global Partners' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  })
};

const statsMotion = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.8, duration: 0.7 },
};

const scrollIndicatorMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 1.2 },
};

const bounceMotion = {
  animate: { y: [0, 8, 0] },
  transition: { repeat: Infinity, duration: 2 },
};

const heroBackground = {
  background: 'radial-gradient(ellipse at top center, #0C1A3A 0%, #060B1E 60%, #040714 100%)'
};

const radialOverlay = {
  backgroundImage: 'radial-gradient(circle at 50% 30%, rgba(59,130,246,0.3) 0%, transparent 50%)'
};

const cardDetails = [
  { label: 'Project Type', value: 'Strategic Resource + Digital Infrastructure' },
  { label: 'Core Thesis', value: 'High-concentration iodine backed by scalable project architecture' },
  { label: 'Target', value: 'Industrial partnerships, capital introduction, market positioning' },
];

function HeroContent() {
  return (
    <div>
      <motion.div
        custom={0} initial="hidden" animate="visible" variants={fadeUp}
        className="mb-6 inline-flex items-center gap-2 rounded-sm border border-[rgba(59,130,246,0.25)] bg-[rgba(59,130,246,0.08)] px-4 py-1.5"
      >
        <img src={LOGO_URL} alt="IODX" className="h-5 w-5 rounded-sm object-cover" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#60A5FA]">
          IBL Elements Inc. &mdash; IODX Digital Reserve
        </span>
      </motion.div>

      <motion.h1
        custom={1} initial="hidden" animate="visible" variants={fadeUp}
        data-testid="hero-headline"
        className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#F1F5F9] leading-none"
      >
        Tokenized positioning for a
        <span className="text-[#3B82F6]"> strategic iodine</span> resource.
      </motion.h1>

      <motion.p
        custom={2} initial="hidden" animate="visible" variants={fadeUp}
        data-testid="hero-subtitle"
        className="mt-6 max-w-xl text-lg leading-relaxed text-[#94A3B8]"
      >
        A premium platform uniting iodine resource strength, proprietary extraction technology, and digital-asset infrastructure for institutional and investor engagement.
      </motion.p>

      <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
        <a
          href="#contact" data-testid="hero-cta-primary"
          className="rounded-sm bg-[#3B82F6] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:bg-[#60A5FA] hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5"
        >
          Request Investor Materials
        </a>
        <a
          href="#overview" data-testid="hero-cta-secondary"
          className="rounded-sm border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-[#F1F5F9] transition-all duration-300 hover:bg-white/10 hover:border-[#3B82F6]/30 hover:-translate-y-0.5"
        >
          Explore Project
        </a>
      </motion.div>
    </div>
  );
}

function HeroCard() {
  return (
    <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp} className="hidden lg:block">
      <div className="rounded-sm border border-[rgba(59,130,246,0.15)] bg-[#0A1628]/80 p-8 backdrop-blur-xl shadow-2xl shadow-blue-900/10">
        <div className="flex items-center gap-3 mb-4">
          <img src={LOGO_URL} alt="IODX" className="h-12 w-12 rounded-md object-cover" />
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#60A5FA]">Investor Positioning</div>
            <div className="mt-1 text-2xl font-bold tracking-tight text-[#F1F5F9]">IODX</div>
          </div>
        </div>
        <div className="text-sm text-[#94A3B8]">Iodine Digital Reserve</div>
        <div className="mt-6 space-y-3">
          {cardDetails.map((d) => (
            <div key={d.label} className="rounded-sm bg-[#060B1E]/70 border border-white/5 p-4">
              <div className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">{d.label}</div>
              <div className="mt-1 text-sm font-medium text-[#F1F5F9]">{d.value}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function HeroStats() {
  return (
    <motion.div {...statsMotion} className="mt-20 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl" data-testid="hero-stats">
      {stats.map((stat) => (
        <div key={stat.label} className="border-l-2 border-[#3B82F6]/40 pl-5">
          <div className="text-3xl font-bold text-[#F1F5F9]">
            <AnimatedCounter end={stat.end} suffix={stat.suffix} label={stat.label} />
          </div>
          <div className="mt-1 text-xs uppercase tracking-[0.15em] text-[#94A3B8]">{stat.label}</div>
        </div>
      ))}
    </motion.div>
  );
}

export default function HeroSection() {
  return (
    <section
      id="hero" data-testid="hero-section"
      className="relative min-h-[calc(100vh-104px)] flex items-center overflow-hidden"
      style={heroBackground}
    >
      <div className="hero-glow hero-glow-primary" />
      <div className="hero-glow hero-glow-secondary" />
      <div className="absolute inset-0 opacity-[0.04]" style={radialOverlay} />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 w-full py-32 lg:py-0">
        <div className="grid items-center gap-16 lg:grid-cols-[1.2fr,0.8fr]">
          <HeroContent />
          <HeroCard />
        </div>
        <HeroStats />

        <motion.div {...scrollIndicatorMotion} className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <motion.div {...bounceMotion}>
            <ArrowDown className="w-5 h-5 text-[#3B82F6]/50" strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
